// Master Service Areas data model for Innervation Energy Group.
// Designed to scale: add a new state to STATE_DATA and it automatically
// appears on the map, browse grids, comparison table, and gets its own page.

import { INDUSTRIES } from "@/data/content";

export const SERVICE_META = {
  electricity: {
    key: "electricity",
    label: "Commercial Electricity",
    short: "Electricity",
    color: "#0052FF",
    icon: "Zap",
    solution: "/solutions/commercial-electricity",
  },
  gas: {
    key: "gas",
    label: "Commercial Natural Gas",
    short: "Natural Gas",
    color: "#00C853",
    icon: "Flame",
    solution: "/solutions/commercial-natural-gas",
  },
  battery: {
    key: "battery",
    label: "Virtual Power Plant Network",
    short: "VPP Network",
    color: "#06B6D4",
    icon: "BatteryCharging",
    solution: "/solutions/battery-hosting",
  },
};

export const FEATURED_SLUGS = ["tx", "il", "ny", "pa", "fl"];

// Which existing Industry detail page each label maps to (future-proof mapping).
export const INDUSTRY_LINKS = {
  Hotels: "hospitality",
  Restaurants: "hospitality",
  Hospitals: "healthcare",
  Manufacturing: "manufacturing",
  Warehousing: "warehousing-logistics",
  "Office Buildings": "commercial-real-estate",
  Retail: "commercial-real-estate",
  "Grocery Stores": "commercial-real-estate",
  "Convenience Stores": "commercial-real-estate",
  "Gas Stations": "commercial-real-estate",
  Laundromats: "commercial-real-estate",
  "Data Centers": "data-centers",
};

const DEFAULT_INDUSTRIES = [
  "Hotels",
  "Hospitals",
  "Manufacturing",
  "Office Buildings",
  "Retail",
  "Warehousing",
  "Data Centers",
  "Restaurants",
];

const GAS_INDUSTRIES = [
  "Manufacturing",
  "Hotels",
  "Hospitals",
  "Restaurants",
  "Grocery Stores",
  "Laundromats",
  "Convenience Stores",
];

// Raw configuration. electricity/gas = false OR an object { note?, requirement? }.
export const STATE_DATA = {
  tx: {
    name: "Texas",
    abbr: "TX",
    grid: "ERCOT",
    electricity: {},
    gas: {},
    battery: true,
    industries: ["Manufacturing", "Data Centers", "Hotels", "Hospitals", "Warehousing", "Retail", "Restaurants", "Office Buildings"],
  },
  il: {
    name: "Illinois",
    abbr: "IL",
    grid: "PJM & MISO",
    electricity: {},
    gas: {},
    battery: true,
    industries: ["Manufacturing", "Warehousing", "Hospitals", "Hotels", "Office Buildings", "Data Centers", "Retail", "Grocery Stores"],
  },
  ny: { name: "New York", abbr: "NY", grid: "NYISO", electricity: {}, gas: {}, battery: false },
  pa: { name: "Pennsylvania", abbr: "PA", grid: "PJM", electricity: {}, gas: {}, battery: false },
  oh: { name: "Ohio", abbr: "OH", grid: "PJM", electricity: {}, gas: {}, battery: false },
  md: { name: "Maryland", abbr: "MD", grid: "PJM", electricity: {}, gas: {}, battery: false },
  ct: { name: "Connecticut", abbr: "CT", grid: "ISO-NE", electricity: {}, gas: {}, battery: false },
  nj: { name: "New Jersey", abbr: "NJ", grid: "PJM", electricity: {}, gas: {}, battery: false },
  de: { name: "Delaware", abbr: "DE", grid: "PJM", electricity: {}, gas: {}, battery: false },
  mi: { name: "Michigan", abbr: "MI", grid: "MISO", electricity: {}, gas: {}, battery: false },
  ri: { name: "Rhode Island", abbr: "RI", grid: "ISO-NE", electricity: {}, gas: {}, battery: false },
  dc: { name: "Washington DC", abbr: "DC", grid: "PJM", electricity: {}, gas: {}, battery: false },
  in: { name: "Indiana", abbr: "IN", grid: "MISO", electricity: false, gas: {}, battery: false },
  ga: { name: "Georgia", abbr: "GA", grid: null, electricity: false, gas: {}, battery: false },
  fl: { name: "Florida", abbr: "FL", grid: null, electricity: false, gas: {}, battery: false },
  co: {
    name: "Colorado",
    abbr: "CO",
    grid: null,
    electricity: false,
    gas: { note: "Minimum 50,000 therms annually", requirement: "Requires twelve months of gas bills to qualify" },
    battery: false,
  },
  wi: {
    name: "Wisconsin",
    abbr: "WI",
    grid: null,
    electricity: false,
    gas: { note: "Minimum 50,000 therms annually", requirement: "Requires twelve months of gas bills to qualify" },
    battery: false,
  },
  va: {
    name: "Virginia",
    abbr: "VA",
    grid: "PJM",
    electricity: { note: "Available only for exceptionally large commercial accounts" },
    gas: { note: "Minimum 75,000 therms annually" },
    battery: false,
  },
};

export const SUPPORTED_SLUGS = Object.keys(STATE_DATA);

// ---------------------------- Helpers ----------------------------
export function getServices(slug) {
  const s = STATE_DATA[slug];
  if (!s) return null;
  return { electricity: !!s.electricity, gas: !!s.gas, battery: !!s.battery };
}

export function tierColor(slug, active = false) {
  const s = getServices(slug);
  if (!s) return active ? "#243056" : "#131e3b";
  if (s.battery) return active ? "#3ad2ee" : "#06B6D4";
  if (s.electricity && s.gas) return active ? "#3f7bff" : "#0052FF";
  if (s.gas) return active ? "#33d67d" : "#00C853";
  return active ? "#243056" : "#131e3b";
}

export function shortLabels(s) {
  const a = [];
  if (s.electricity) a.push("Electricity");
  if (s.gas) a.push("Natural Gas");
  if (s.battery) a.push("Virtual Power Plant hosting");
  return a;
}

export function availableServices(s) {
  const list = [];
  if (s.electricity) list.push({ key: "electricity", note: s.electricity.note });
  if (s.gas) list.push({ key: "gas", note: s.gas.note, requirement: s.gas.requirement });
  if (s.battery) list.push({ key: "battery" });
  return list;
}

function relatedArticleSlugs(s) {
  const slugs = ["what-is-energy-deregulation"];
  if (s.electricity) slugs.push("fixed-vs-indexed-pricing", "understanding-demand-charges", "understanding-capacity-charges");
  if (s.gas) slugs.push("commercial-natural-gas-procurement-explained");
  if (s.battery) slugs.push("commercial-battery-hosting-explained");
  return Array.from(new Set(slugs)).slice(0, 4);
}

function generateIntro(s) {
  const labels = shortLabels(s);
  const joined = labels.join(", ").replace(/,([^,]*)$/, " and$1");
  if (s.electricity && s.gas) {
    return `${s.name} is a competitive commercial energy market. Innervation Energy Group helps businesses across ${s.name} procure ${joined} with independent, strategic guidance.`;
  }
  return `Commercial natural gas supply is open to competition in ${s.name}. Innervation Energy Group helps qualifying businesses procure ${joined} with independent, strategic guidance.`;
}

function generateOverview(s) {
  const labels = shortLabels(s);
  return `${s.name} supports ${labels.join(", ").replace(/,([^,]*)$/, " and$1")}${s.grid ? ` within the ${s.grid} market` : ""}.`;
}

function generateMarket(s) {
  const secs = [];
  let overview;
  if (s.electricity && s.gas) {
    overview = `${s.name} is a competitive energy market, which means eligible commercial organizations can choose their electricity and natural gas suppliers rather than defaulting to a single regulated provider. That choice creates real opportunity — and real complexity. Innervation Energy Group helps businesses in ${s.name} navigate it with independent, transparent guidance.`;
  } else {
    overview = `In ${s.name}, commercial natural gas supply is open to competition, allowing qualifying businesses to choose their gas supplier, while electricity remains served by the regulated local utility. Innervation Energy Group helps commercial gas users in ${s.name} procure supply strategically.`;
  }
  secs.push({ heading: `Commercial energy in ${s.name}`, body: overview });

  if (s.electricity) {
    let body = `Wholesale electricity in ${s.name} is coordinated by ${s.grid || "the regional grid operator"}, which balances supply and demand across the market. Commercial electricity procurement here focuses on comparing suppliers, selecting the right contract structure — fixed, index, or blended — and timing purchases against market conditions.`;
    if (s.electricity.note) body += ` Note: in ${s.name}, commercial electricity is ${s.electricity.note.toLowerCase()}.`;
    secs.push({ heading: "Electricity procurement", body });
  }

  if (s.gas) {
    let body = `Commercial natural gas procurement in ${s.name} centers on securing competitive supply and managing seasonal price volatility through disciplined, often layered, purchasing aligned to your consumption curve.`;
    if (s.gas.note) {
      body += ` Qualification: ${s.gas.note}${s.gas.requirement ? ` — ${s.gas.requirement.toLowerCase()}.` : "."}`;
    } else {
      body += ` To provide an accurate, competitive quote we typically review twelve months of your gas bills.`;
    }
    secs.push({ heading: "Natural gas procurement", body });
  }

  if (s.battery) {
    secs.push({
      heading: "Virtual Power Plant Network",
      body: `${s.name} is one of the initial markets where Innervation Energy Group is partnering with commercial property owners to host battery energy storage systems as part of our growing Virtual Power Plant Network. Owners of suitable sites near grid infrastructure may be able to generate long-term value from underutilized land or electrical infrastructure while supporting a more reliable, resilient grid.`,
    });
  }

  secs.push({
    heading: "Market considerations",
    body: `Energy decisions in ${s.name} should account for contract timing, seasonality, capacity and transmission costs, and your specific load profile. Our role is to translate market conditions into concrete guidance for your facilities — and to stay engaged as conditions evolve.`,
  });
  return secs;
}

function generateFaqs(s) {
  const f = [];
  const services = shortLabels(s).map((x) => x.toLowerCase());
  const joined = services.join(", ").replace(/,([^,]*)$/, " and$1");
  f.push({
    q: `What energy services does Innervation Energy Group offer in ${s.name}?`,
    a: `In ${s.name}, we offer ${joined}. Every engagement is grounded in independent, transparent advice.`,
  });

  if (s.electricity && s.gas) {
    f.push({
      q: `Is commercial energy deregulated in ${s.name}?`,
      a: `Yes. ${s.name} allows eligible commercial organizations to choose both their electricity and natural gas suppliers${s.grid ? `, with wholesale electricity coordinated by ${s.grid}` : ""}.`,
    });
  } else {
    f.push({
      q: `Can I choose my energy supplier in ${s.name}?`,
      a: `${s.name} offers commercial natural gas supplier choice. Electricity remains served by the regulated local utility, so our work in ${s.name} focuses on natural gas procurement.`,
    });
  }

  if (s.gas) {
    f.push({
      q: `What do I need to get a natural gas quote in ${s.name}?`,
      a: `We typically review twelve months of gas bills to understand your usage${s.gas.note ? `. ${s.gas.note}${s.gas.requirement ? ` (${s.gas.requirement.toLowerCase()})` : ""}.` : " and provide an accurate, competitive quote."}`,
    });
  }

  if (s.electricity && s.electricity.note) {
    f.push({
      q: `Is commercial electricity available to my business in ${s.name}?`,
      a: `In ${s.name}, commercial electricity is ${s.electricity.note.toLowerCase()}. Contact us and we'll confirm whether your accounts qualify.`,
    });
  }

  if (s.battery) {
    f.push({
      q: `How do I know if my property qualifies to host in ${s.name}?`,
      a: `We screen sites in ${s.name} for our Virtual Power Plant Network based on location, available acreage or rooftop space, electrical infrastructure, and proximity to substations and transmission. Reach out with your property details and we'll evaluate the opportunity.`,
    });
  }

  f.push({
    q: `How does Innervation Energy Group get paid?`,
    a: `Our engagement models are transparent and disclosed upfront, so there are no surprises. We act as your independent advisor — not a single supplier's sales channel.`,
  });
  return f;
}

// Returns an enriched state object (with generated content) or null.
export function getState(slug) {
  const raw = STATE_DATA[slug];
  if (!raw) return null;
  const s = { ...raw, slug };
  s.intro = raw.intro || generateIntro(s);
  s.overview = raw.overview || generateOverview(s);
  s.market = raw.market || generateMarket(s);
  s.faqs = raw.faqs || generateFaqs(s);
  s.industries = raw.industries || (s.electricity ? DEFAULT_INDUSTRIES : GAS_INDUSTRIES);
  s.relatedArticleSlugs = relatedArticleSlugs(s);
  return s;
}

export function listStates() {
  return SUPPORTED_SLUGS.map((slug) => ({ slug, ...STATE_DATA[slug] })).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
}

export function requirementText(s) {
  const parts = [];
  if (s.electricity && s.electricity.note) parts.push(`Electricity: ${s.electricity.note}`);
  if (s.gas && s.gas.note) parts.push(`Natural Gas: ${s.gas.note}`);
  return parts.length ? parts.join(" · ") : "—";
}

export function industryLink(label) {
  const slug = INDUSTRY_LINKS[label];
  return slug && INDUSTRIES.some((i) => i.slug === slug) ? `/industries/${slug}` : "/industries";
}

export const LANDING_FAQS = [
  {
    q: "Why does energy service availability vary from state to state?",
    a: "Energy markets are regulated at the state level. Some states have deregulated electricity and/or natural gas, allowing commercial customers to choose their supplier; others remain regulated with a single utility. Our services follow where competition — and opportunity — exists.",
  },
  {
    q: "What does it mean for a market to be 'deregulated'?",
    a: "In a deregulated (competitive) market, the local utility still delivers energy over its poles, wires, and pipes, but you can choose who supplies the actual electricity or gas. That choice is where a commercial energy advisor adds value.",
  },
  {
    q: "How can my property join the Virtual Power Plant Network?",
    a: "We partner with commercial property owners to host battery energy storage systems as part of our growing Virtual Power Plant Network, with initial site evaluations focused on Texas and Illinois. If you own or control commercial land or facilities, we can assess your site's hosting potential.",
  },
  {
    q: "My state isn't highlighted — can you still help?",
    a: "Possibly. Market access changes over time, and some services apply only to large or high-usage accounts. Contact us with your location and usage and we'll confirm current availability.",
  },
  {
    q: "Do you work with businesses that operate in multiple states?",
    a: "Yes. Many of our clients operate across several states and utilities. We consolidate procurement and renewals into a single, coordinated strategy across your entire footprint.",
  },
];
